import {
  AttendanceFrequencyResponse,
  AttendanceSessionEnum,
  DEFAULT_DATE_FORMAT_FNS,
  GroupMin,
  GroupType,
  useGetGroupsQuery,
} from '@schooly/api';
import { useAuth } from '@schooly/components/authentication';
import { SchoolUserRole } from '@schooly/constants';
import { useAgeGroups } from '@schooly/hooks/use-school-properties';
import { format } from 'date-fns';
import { useMemo } from 'react';

import {
  AttendanceGridSession,
  AttendanceGridTrackingPoint,
} from '../../../Attendance/AttendanceTable/common/types';
import { useAttendanceCountsPoints } from './useAttendanceCountsPoints';
import {
  getAttendanceFrequencyConfig,
  resolveAttendanceSessionState,
} from '../../../Attendance/attendance-utils';
import { buildStaffGroupsQueryParams } from '../utils';

export type GroupTrackingTag = {
  key: string;
  label: string | null;
  completed: boolean;
  session: AttendanceGridSession | null;
  periodId: string | null;
};

const isPointVisible = (
  point: AttendanceGridTrackingPoint,
  isTutorGroup: boolean,
  isSubjectResponsible: Record<AttendanceSessionEnum, boolean>,
): boolean => {
  if (point.period) return true;
  if (!point.session) return false;

  const state = resolveAttendanceSessionState({
    isTutorGroup,
    sessionExists: true,
    isCompleted: point.complete,
    isSubjectResponsible: isSubjectResponsible[point.session],
  });

  return state === 'completed' || state === 'required';
};

const toTag = (point: AttendanceGridTrackingPoint): GroupTrackingTag => ({
  key: point.key,
  label: point.label || null,
  completed: point.complete,
  session: point.session,
  periodId: point.period?.id ?? null,
});

const ALL_COMPLETED_TAG: GroupTrackingTag = {
  key: 'all-completed',
  label: null,
  completed: true,
  session: null,
  periodId: null,
};

const resolveGroupTags = (
  group: GroupMin,
  frequency: AttendanceFrequencyResponse | null,
  points: AttendanceGridTrackingPoint[] | undefined,
): GroupTrackingTag[] => {
  if (!points) return [];

  const { isSubjectResponsible } = getAttendanceFrequencyConfig(frequency);
  const isTutorGroup = group.group_type === GroupType.TutorGroup;

  const visible = points.reduce<GroupTrackingTag[]>((acc, point) => {
    if (isPointVisible(point, isTutorGroup, isSubjectResponsible)) {
      acc.push(toTag(point));
    }

    return acc;
  }, []);

  if (isTutorGroup || visible.length === 0) return visible;

  const next = visible.find((tag) => !tag.completed);

  return next ? [next] : [ALL_COMPLETED_TAG];
};

interface UseGroupsWidgetQueryParams {
  canViewAttendance: boolean;
}

export interface GroupsWidgetData {
  tutorGroups: GroupMin[];
  otherGroups: GroupMin[];
  tagsByGroupId: Record<string, GroupTrackingTag[]>;
  isLoading: boolean;
  hasNoGroups: boolean;
}

export const useGroupsWidgetData = ({
  canViewAttendance,
}: UseGroupsWidgetQueryParams): GroupsWidgetData => {
  const { relationId = '', schoolId = '' } = useAuth();

  const today = useMemo(() => format(new Date(), DEFAULT_DATE_FORMAT_FNS), []);

  // Paired with `useAssessmentsWidgetQuery` (same query, `refetchOnMount: false` there).
  const { data: groupsData, isLoading: isGroupsLoading } = useGetGroupsQuery(
    buildStaffGroupsQueryParams({ schoolId, relationId }),
    { refetchOnMount: 'always', enabled: !!relationId },
  );

  const { tutorGroups, otherGroups, groupIds } = useMemo(() => {
    const [tutorGroups, otherGroups] = (groupsData?.pages ?? []).reduce<[GroupMin[], GroupMin[]]>(
      ([tutors, others], page) => {
        for (const g of page.results) {
          (g.group_type === GroupType.TutorGroup ? tutors : others).push(g);
        }
        return [tutors, others];
      },
      [[], []],
    );

    return {
      tutorGroups,
      otherGroups,
      groupIds: [...tutorGroups, ...otherGroups].map((g) => g.id),
    };
  }, [groupsData?.pages]);

  const canGetAttendance = canViewAttendance && !!schoolId && groupIds.length > 0;

  const { pointsByGroupId, isLoading: countsLoading } = useAttendanceCountsPoints({
    schoolId,
    date: today,
    groupIds,
    enabled: canGetAttendance,
  });

  const { activeAgeGroups, isLoading: ageGroupsLoading } = useAgeGroups(
    { schoolId, userType: SchoolUserRole.Student },
    { enabled: canGetAttendance },
  );

  const tagsByGroupId = useMemo(() => {
    const frequencyByAgeGroupId = new Map(
      activeAgeGroups.map((ageGroup) => [ageGroup.id, ageGroup.attendance_frequency ?? null]),
    );

    const getFrequency = (group: GroupMin): AttendanceFrequencyResponse | null => {
      const ageGroup = group.age_groups[0];
      if (!ageGroup) return null;

      return ageGroup.attendance_frequency ?? frequencyByAgeGroupId.get(ageGroup.id) ?? null;
    };

    return [...tutorGroups, ...otherGroups].reduce<Record<string, GroupTrackingTag[]>>(
      (acc, group) => {
        acc[group.id] = resolveGroupTags(group, getFrequency(group), pointsByGroupId.get(group.id));
        return acc;
      },
      {},
    );
  }, [pointsByGroupId, activeAgeGroups, tutorGroups, otherGroups]);

  const isAttendanceDataLoading = countsLoading || (canGetAttendance && ageGroupsLoading);
  const isLoading = isGroupsLoading || isAttendanceDataLoading;
  const hasNoGroups = groupsData?.pages[0]?.count === 0;

  return {
    tutorGroups,
    otherGroups,
    tagsByGroupId,
    isLoading,
    hasNoGroups,
  };
};
